import React, { useCallback, useEffect, useState } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L, { Marker as MarkerProp } from 'leaflet';
import 'leaflet-rotate';
import { caution, cleaning, equipments, greening, robo } from './raw_svg';
import { useAnimationFrame } from '../../../hook/use_animation_frame';
import { Robot, Worker } from '../../../model/workers_response';
import { RobotDetail, WorkerDetail } from '../workers_icon/worker_details';
import {getRobotState} from "../../../constant";

const diff = (a: [number, number], b: [number, number]): [number, number] => {
  return [(a[0] - b[0]), (a[1] - b[1])];
};
const isZero = (a: [number, number]) => {
  // 丸め込みを考慮
  return Math.abs(a[0]) < 0.00001 && Math.abs(a[1]) < 0.00001;
};

/**
 * マップアイコンのプロパティ
 */
type MapIconProps = {
  position: [number, number];
  type: string;
  isCaution?: boolean;
  isStop?: boolean;
  children?: React.ReactNode;
};

/**
 * マップアイコン
 * @param position
 * @param type
 * @param children
 * @param isCaution
 * @param isStop
 * @constructor
 */
export const MapIcon: React.FC<MapIconProps> = ({ position, type, children, isCaution = false, isStop = false }) => {
  const [pos, setPos] = useState(position);
  const [running, setRunning] = useState(false);
  const popupRef = React.useRef<MarkerProp>(null);

  const loop = useCallback((dt: number) => {
    const df = diff(position, pos);

    if (isZero(df)) {
      setRunning(false);
    }
    const speed = 2;
    const nextPos: [number, number] = [pos[0] + df[0] * speed * dt, pos[1] + df[1] * speed * dt];

    setPos(nextPos);
  }, [pos, position]);

  useAnimationFrame(running, loop);

  useEffect(() => {
    if (!isZero(diff(pos, position))) {
      setRunning(true);
    }
  }, [position, pos, loop]);

  const icon = (type: string) => {
    switch (type) {
      case 'robot':
        return robo;
      case 'cleaning':
        return cleaning;
      case 'equipment':
        return equipments;
      case 'greening':
        return greening;
      default:
        return robo;
    }
  };


  return (
      // eslint-disable-next-line no-template-curly-in-string
      <Marker ref={popupRef} autoPanOnFocus={false} position={pos} icon={new L.DivIcon({ className: 'mapIcon', iconSize: [50, 50], iconAnchor: [25, 0], html: `<div>${icon(type)((isStop ? 'offline' : '') + ' ' + (isCaution ? 'alert' : ''))}${isCaution ? caution : ''}</div>` })}>
          <Popup>
              <div>
                  {children}
              </div>
          </Popup>
      </Marker>);
};

/**
 * 作業員のマップアイコン
 * @param worker
 * @constructor
 */
export const WorkerMapIcon: React.FC<{ worker: Worker }> = ({ worker }) => {
  return (
        <MapIcon position={[worker.position.lat, worker.position.lng]} isStop={worker.status === 'BREAK'} type={worker.businessType}>
          <WorkerDetail worker={worker}/>
        </MapIcon>
  );
};

/**
 * ロボットのマップアイコン
 * @param robot
 * @constructor
 */
export const RobotMapIcon: React.FC<{ robot: Robot }> = ({ robot }) => {
  const { isStop, isCaution } = getRobotState(robot.status.operationState);
  return (
        <MapIcon position={[robot.position.lat, robot.position.lng]} isStop={isStop} isCaution={isCaution} type={'robot'}>
          <RobotDetail robot={robot}/>
        </MapIcon>
  );
};
